// Pure helpers over one department's library (folders + documents, as returned
// by GET /api/department-documents). Kept free of React so they can be unit
// tested — see libraryTree.test.ts.

export interface LibraryFolder {
  _id: string;
  name: string;
  parent: string | null;
}

export interface LibraryDocument {
  _id: string;
  name: string;
  folder: string | null;
  file?: { originalName?: string };
}

export const ROOT_LABEL = 'מסמכי המחלקה';

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, 'he');

/** The chain of folders from the root down to `folderId` (inclusive). */
export const folderPath = (folders: LibraryFolder[], folderId: string | null): LibraryFolder[] => {
  const byId = new Map(folders.map(f => [f._id, f]));
  const path: LibraryFolder[] = [];
  const seen = new Set<string>();
  let current = folderId ? byId.get(folderId) : undefined;
  // `seen` guards against a corrupt parent cycle hanging the page.
  while (current && !seen.has(current._id)) {
    seen.add(current._id);
    path.unshift(current);
    current = current.parent ? byId.get(current.parent) : undefined;
  }
  return path;
};

/** "מסמכי המחלקה / נהלים / סיעוד" — where a document or folder lives. */
export const folderPathLabel = (folders: LibraryFolder[], folderId: string | null): string =>
  [ROOT_LABEL, ...folderPath(folders, folderId).map(f => f.name)].join(' / ');

/** What one folder shows: its direct subfolders and documents, each sorted by name. */
export const folderContents = (
  folders: LibraryFolder[],
  documents: LibraryDocument[],
  folderId: string | null
) => ({
  folders: folders.filter(f => (f.parent || null) === folderId).sort(byName),
  documents: documents.filter(d => (d.folder || null) === folderId).sort(byName),
});

/**
 * Search the whole library, whatever folder is open. Every word of the term has
 * to appear in the name (or the original file name), in any order.
 */
export const searchLibrary = (
  folders: LibraryFolder[],
  documents: LibraryDocument[],
  term: string
) => {
  const words = term.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return { folders: [], documents: [] };
  const matches = (...texts: (string | undefined)[]) => {
    const haystack = texts.filter(Boolean).join(' ').toLowerCase();
    return words.every(word => haystack.includes(word));
  };
  return {
    folders: folders.filter(f => matches(f.name)).sort(byName),
    documents: documents.filter(d => matches(d.name, d.file?.originalName)).sort(byName),
  };
};

/**
 * Every folder as a destination option, indented by depth, root first. Pass
 * `excludeSubtreeOf` when moving a folder: it cannot go into itself or below.
 */
export const folderOptions = (folders: LibraryFolder[], excludeSubtreeOf: string | null = null) => {
  const options: { id: string | null; label: string }[] = [{ id: null, label: ROOT_LABEL }];
  const walk = (parentId: string | null, depth: number) => {
    folders
      .filter(f => (f.parent || null) === parentId && f._id !== excludeSubtreeOf)
      .sort(byName)
      .forEach(folder => {
        options.push({ id: folder._id, label: `${'   '.repeat(depth)}${folder.name}` });
        walk(folder._id, depth + 1);
      });
  };
  walk(null, 1);
  return options;
};

/** What is being dragged: a document (lives in `from`) or a folder (lives in `from`). */
export interface DragItem {
  kind: 'document' | 'folder';
  id: string;
  from: string | null;
}

/**
 * May `item` be dropped into `targetFolderId` (null = the root)? Not where it
 * already is, and a folder not into itself or anything below it — the server
 * refuses that too, this just keeps the drop target from lighting up.
 */
export const canDropInto = (folders: LibraryFolder[], item: DragItem | null, targetFolderId: string | null): boolean => {
  if (!item) return false;
  if ((item.from || null) === targetFolderId) return false;
  if (item.kind === 'folder') {
    if (item.id === targetFolderId) return false;
    if (folderPath(folders, targetFolderId).some(f => f._id === item.id)) return false;
  }
  return true;
};

/** The most of a folder's description its tile shows; the open folder shows all of it. */
export const TILE_DESCRIPTION_MAX = 150;

/** A folder description cut to fit its tile: up to 150 characters, then "…". */
export const tileDescription = (description: string | undefined | null): string => {
  const text = (description || '').trim();
  // By code point, so a cut never splits an emoji or a surrogate pair.
  const chars = Array.from(text);
  return chars.length <= TILE_DESCRIPTION_MAX ? text : `${chars.slice(0, TILE_DESCRIPTION_MAX).join('').trimEnd()}…`;
};

/** One page of an already-loaded list (local search results). */
export const paginate = <T>(items: T[], page: number, pageSize: number) => {
  const pages = Math.ceil(items.length / pageSize);
  return { items: items.slice((page - 1) * pageSize, page * pageSize), pages, total: items.length };
};
